import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { barterApi } from '@/services/api'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@/hooks/useToast'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import Badge from '@/components/ui/Badge'
import Loading from '@/components/ui/Loading'
import Pagination from '@/components/ui/Pagination'
import ResponsiveTable from '@/components/ui/ResponsiveTable'
import { formatCurrency, formatDate } from '@/utils/format'
import { Search, Plus, Edit, Trash2, Eye, ArrowUp, ArrowDown, Info } from 'lucide-react'
import type { Barter } from '@/types'

type SortColumn = 'title' | 'type' | 'dollar_value' | 'status' | 'created_at' | 'city' | 'country'
type SortOrder = 'asc' | 'desc'

export default function MyBarters() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const toast = useToast()
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [visibilityFilter, setVisibilityFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [sortBy, setSortBy] = useState<SortColumn>('created_at')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      if (search !== debouncedSearch) {
        setCurrentPage(1) // Reset to page 1 on new search
      }
    }, 500) // 500ms debounce

    return () => clearTimeout(timer)
  }, [search])

  const { data, isLoading } = useQuery({
    queryKey: ['my-barters', {
      search: debouncedSearch,
      type: typeFilter,
      status: statusFilter,
      page: currentPage,
      sort_by: sortBy,
      sort_order: sortOrder
    }],
    queryFn: () => barterApi.list({
      my_barters: 'true',
      search: debouncedSearch,
      type: typeFilter,
      status: statusFilter,
      page: currentPage,
      limit: 10,
      sort_by: sortBy,
      sort_order: sortOrder
    }),
    placeholderData: (previousData) => previousData,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => barterApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-barters'] })
      toast.success('Barter deleted successfully')
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to delete barter')
    },
  })

  const barters = (data?.data as any)?.barters || []
  const totalPages = (data?.data as any)?.pages || 1

  // Filter by visibility on client side
  const filteredBarters = visibilityFilter
    ? barters.filter((b: Barter) => b.visibility === visibilityFilter)
    : barters

  const handleSort = (column: SortColumn) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortOrder('asc')
    }
    setCurrentPage(1) // Reset to first page when sorting changes
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSearchChange = (value: string) => {
    setSearch(value)
  }

  const handleFilterChange = (filterType: 'type' | 'status' | 'visibility', value: string) => {
    if (filterType === 'type') {
      setTypeFilter(value)
    } else if (filterType === 'status') {
      setStatusFilter(value)
    } else {
      setVisibilityFilter(value)
    }
    setCurrentPage(1) // Reset to first page when filter changes
  }

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this barter?')) {
      await deleteMutation.mutateAsync(id)
    }
  }

  const SortIcon = ({ column }: { column: SortColumn }) => {
    if (sortBy !== column) {
      return <ArrowUp className="w-3 h-3 text-gray-300" />
    }
    return sortOrder === 'asc' ? (
      <ArrowUp className="w-3 h-3 text-primary-600" />
    ) : (
      <ArrowDown className="w-3 h-3 text-primary-600" />
    )
  }

  if (isLoading && !data) return <Loading />

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">My Barters</h1>
        <Button onClick={() => navigate('/dashboard/barters/create')}>
          <Plus className="w-4 h-4 mr-2" />
          Create Barter
        </Button>
      </div>

      <Card>
        <div className="space-y-4 mb-6">
          {/* Search Bar */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 min-w-0">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search my barters..."
                  className="input pl-10 w-full"
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </div>
            </div>
            <select className="select w-full md:w-auto" value={typeFilter} onChange={(e) => handleFilterChange('type', e.target.value)}>
              <option value="">All Types</option>
              <option value="goods">Goods</option>
              <option value="services">Services</option>
            </select>
            <select className="select w-full md:w-auto" value={statusFilter} onChange={(e) => handleFilterChange('status', e.target.value)}>
              <option value="">All Statuses</option>
              <option value="open">Open</option>
              <option value="ready">Ready</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="closed">Closed</option>
            </select>
            <select className="select w-full md:w-auto" value={visibilityFilter} onChange={(e) => handleFilterChange('visibility', e.target.value)}>
              <option value="">All Visibility</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <ResponsiveTable>
          <table className="table">
            <thead className="table-header">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('title')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Title</span>
                    <SortIcon column="title" />
                  </div>
                </th>
                <th
                  className="hide-mobile px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('type')}
                  data-priority="5"
                >
                  <div className="flex items-center space-x-1">
                    <span>Type</span>
                    <SortIcon column="type" />
                  </div>
                </th>
                <th
                  className="hide-mobile px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('dollar_value')}
                  data-priority="4"
                >
                  <div className="flex items-center space-x-1">
                    <span>Value</span>
                    <SortIcon column="dollar_value" />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('city')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Location</span>
                    <SortIcon column="city" />
                  </div>
                </th>
                <th className="hide-mobile px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-priority="3">
                  Visibility
                </th>
                <th
                  className="hide-mobile px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('status')}
                  data-priority="2"
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    <SortIcon column="status" />
                  </div>
                </th>
                <th
                  className="hide-mobile px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('created_at')}
                  data-priority="1"
                >
                  <div className="flex items-center space-x-1">
                    <span>Created</span>
                    <SortIcon column="created_at" />
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredBarters.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <p className="text-gray-500 mb-4">No barters found</p>
                    <Button onClick={() => navigate('/dashboard/barters/create')}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Your First Barter
                    </Button>
                  </td>
                </tr>
              ) : (
                filteredBarters.map((barter: Barter) => (
                  <tr key={barter.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div
                        className="font-medium text-gray-900 hover:text-primary-600 cursor-pointer"
                        onClick={() => navigate(`/dashboard/barters/${barter.id}`)}
                      >
                        {barter.title}
                      </div>
                      <div className="text-sm text-gray-500">{barter.description?.substring(0, 50)}...</div>
                    </td>
                    <td className="hide-mobile px-6 py-4 whitespace-nowrap" data-priority="5" data-label="Type">
                      <Badge variant={barter.type === 'goods' ? 'primary' : 'success'}>
                        {barter.type}
                      </Badge>
                    </td>
                    <td className="hide-mobile px-6 py-4 whitespace-nowrap font-medium" data-priority="4" data-label="Value">
                      {formatCurrency(barter.dollar_value)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {barter.city || 'Any'}
                      {barter.state && <>, {barter.state}</>}
                      , {barter.country || 'Any'}
                    </td>
                    <td className="hide-mobile px-6 py-4 whitespace-nowrap" data-priority="3" data-label="Visibility">
                      <Badge variant={barter.visibility === 'active' ? 'success' : 'gray'}>
                        {barter.visibility}
                      </Badge>
                    </td>
                    <td className="hide-mobile px-6 py-4 whitespace-nowrap" data-priority="2" data-label="Status">
                      <Badge status={barter.status}>
                        {barter.status.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="hide-mobile px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-priority="1" data-label="Created">
                      {formatDate(barter.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          className="mobile-details-trigger text-blue-600 hover:text-blue-900"
                          title="View Hidden Details"
                        >
                          <Info className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/dashboard/barters/${barter.id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/dashboard/barters/${barter.id}/edit`)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(barter.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ResponsiveTable>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </Card>

    </div>
  )
}
